// src/routes/AppRouter.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout, setUser } from "../store/authSlice";
import api from "../redux/apis/api";
import { isExplicitSuperAdminRole, normalizeRole } from "../utils/rbacHelper";
import { isDemoReadOnlyUser } from "../utils/demoUser";

import Signup from "../components/Signup";
import Login from "../components/Login";
import Profile from "../components/Profile";
import CreditsTransfer from "../components/admin/credits/CreditsTranfer";
import MessagesPage from "../components/messages/MessagesPage";
import NotificationsPage from "../components/admin/credits/NotificationsPage";

// ✅ NEW IMPORT - Dashboard Component
import Dashboard from "../components/admin/Dashboard";

import ProtectedRoute from "./ProtectedRoute";
import AuthRedirect from "./AuthRedirect";
import MyTasks from "../components/employee/MyTasks";
import GroupDetail from "../components/groups/GroupDetail";
import CreateGroup from "../components/groups/CreateGroup";
import GroupsList from "../components/groups/GroupsList";
import AddDevices from "../components/groups/AddDevices";
import GroupsPageShell from "../components/groups/GroupsPageShell";
import ReportsPage from "../components/reports/ReportsPage";
import LicensesPage from "../components/licenses/LicensesPage";
import ProtocolManagement from "../components/protocol/Protocolmanagement";
import EditGroup from "../components/groups/EditGroup";
import LogisticsModulePage from "../components/logistics/LogisticsModulePage";
import TasksModulePage from "../components/tasks/TasksModulePage";
import DeviceControlWrapper from "../components/admin/devices/device/DeviceControlWrapper";
import InventoryTodo from "../components/inventory/InventoryTodo";
import CameraControlPortal from "../components/cameraV2/CameraControlPortal";
import PublicLiveTrackingPage from "../components/public/PublicLiveTrackingPage";
import RemindersPage from "../components/reminders/RemindersPage";
import RoutesPage from "../components/routes/RoutesPage";
import ExpensePage from "../components/expenses/ExpensePage";
import SensorsPage from "../components/sensors/SensorsPage";
import UsersRolesPage from "../components/users/UsersRolesPage";
import { DEFAULT_USER_ROLE_SECTION } from "../components/users/userRoleConfig";
import GeofencesPage from "../components/geofences/GeofencesPage";
import AdminDashboardRoutePage from "./AdminDashboardRoutePage";
import FuelMonitoringRoutePage from "./FuelMonitoringRoutePage";
import {
  buildAdminDashboardPath,
  getDefaultDashboardTabForRole,
  LIVE_MAP_PATH,
  readDashboardTabFromSearch,
} from "./dashboardNavigation";
import Loader from "../mainframes/Loader";

const WEB_APP_BLOCKED_ROLES = new Set(["viewer", "driver", "field_emp"]);
const WEB_APP_BLOCKED_MESSAGE = "Please login on app";

function DashboardRouteEntry({
  renderAdminDashboard,
  renderDashboard,
  renderWhiteLabelDashboard,
}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedTab = readDashboardTabFromSearch(location.search, "");
  const hasTab = params.has("tab");

  if (requestedTab === "white_label") {
    return renderWhiteLabelDashboard();
  }

  if (requestedTab === "fuel_monitoring") {
    return <Navigate to="/fuel-monitoring" replace />;
  }

  const shouldRenderLegacyDashboard =
    !hasTab || requestedTab === "statics";

  return shouldRenderLegacyDashboard
    ? renderDashboard()
    : renderAdminDashboard();
}

function LegacyAdminDashboardRedirect({ currentUser }) {
  const location = useLocation();
  const fallbackTab = useMemo(
    () => getDefaultDashboardTabForRole(currentUser?.role),
    [currentUser?.role]
  );
  const legacyTab = readDashboardTabFromSearch(location.search, fallbackTab);
  const params = new URLSearchParams(location.search);
  return (
    <Navigate
      replace
      to={buildAdminDashboardPath({
        tab: legacyTab,
        mapStatusFilter: params.get("status"),
        navToken: params.get("nav"),
      })}
    />
  );
}

function DashboardTabAliasRedirect({ tab }) {
  return <Navigate replace to={buildAdminDashboardPath({ tab })} />;
}

export default function AppRouter() {
  const dispatch = useDispatch();

  const token = useSelector((s) => s.auth.token);
  const currentUser = useSelector((s) => s.auth.user);
  const [authChecking, setAuthChecking] = useState(Boolean(token && !currentUser));

  useEffect(() => {
    if (!token) {
      setAuthChecking(false);
      return;
    }

    setAuthChecking(true);

    api
      .get("/auth/profile")
      .then((res) => {
        const normalizedRole = normalizeRole(res?.data?.role);
        if (WEB_APP_BLOCKED_ROLES.has(normalizedRole)) {
          sessionStorage.setItem("webLoginBlockedMessage", WEB_APP_BLOCKED_MESSAGE);
          dispatch(logout());
          return;
        }

        dispatch(setUser(res.data));
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          dispatch(logout());
        }
      })
      .finally(() => {
        setAuthChecking(false);
      });
  }, [token, dispatch]);

  const MESSAGE_ACCESS_ROLES = ["admin", "manager", "hr", "office_emp", "sub_admin", "group_admin", "dealer"];
  const SUPERADMIN_ONLY_ROLES = ["admin"];
  const CREDITS = ["admin", "manager", "hr", "office_emp", "sub_admin", "group_admin"];
  const TASK_MANAGERS = ["admin", "manager", "hr", "office_emp"];
  const FIELD_WORKERS = ["field_emp"];
  const ALL_USERS_STAFF = [
    "admin",
    "manager",
    "hr",
    "field_emp",
    "office_emp",
    "sub_admin",
    "group_admin",
    "driver",
    "client",
    "user",
    "viewer",
  ];
  const GROUP_ACCESS_ROLES = [
    "admin",
    "manager",
    "office_emp",
    "sub_admin",
    "group_admin",
    "client",
    "user",
  ];

  const viewerAtRoot =
    currentUser && normalizeRole(currentUser?.role) === "viewer";
  const demoReadOnly = isDemoReadOnlyUser(currentUser);
  const canAccessWhiteLabel =
    isExplicitSuperAdminRole(currentUser?.role) && !demoReadOnly;

  const renderAdminDashboard = () => (
    <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
      <AdminDashboardRoutePage />
    </ProtectedRoute>
  );

  const renderWhiteLabelDashboard = () => (
    canAccessWhiteLabel ? (
      <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
        <AdminDashboardRoutePage />
      </ProtectedRoute>
    ) : (
      <Navigate to="/dashboard" replace />
    )
  );

  const renderDashboard = () => (
    <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
      <Dashboard />
    </ProtectedRoute>
  );

  if (authChecking) {
    return <Loader fullscreen label="Loading..." background="#f8fafc" />;
  }
  return (
    <>
      {/* ✅ Navbar removed from here (global in App.jsx now) */}

      <Routes>
        <Route
          path="/"
          element={
            currentUser ? (
              viewerAtRoot ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <AuthRedirect>
                <Login />
              </AuthRedirect>
            )
          }
        />
        <Route path="/live-tracking" element={<PublicLiveTrackingPage />} />

        {/* AuthRedirect prevents logged users from opening login/signup */}
        <Route
          path="/login"
          element={
            <AuthRedirect>
              <Login />
            </AuthRedirect>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRedirect>
              <Signup />
            </AuthRedirect>
          }
        />

        {/* ✅ Dashboard Page */}
        <Route
          path="/dashboard"
          element={
            <DashboardRouteEntry
              renderAdminDashboard={renderAdminDashboard}
              renderDashboard={renderDashboard}
              renderWhiteLabelDashboard={renderWhiteLabelDashboard}
            />
          }
        />

        <Route
          path="/fuel-monitoring"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <FuelMonitoringRoutePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/assignments"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="assignments" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/dashboard"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="statics" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/map"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="map" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/history/playback"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_playback" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/history/logs"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_logs" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/history/events"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_events" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/history/parking"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_parking" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/white-label"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              <DashboardTabAliasRedirect tab="white_label" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/users"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="users" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/devices"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="devices" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vehicles"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="devices" />
            </ProtectedRoute>
          }
        />

        <Route
          path={LIVE_MAP_PATH}
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="map" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/history/playback"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_playback" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/history/logs"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_logs" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/history/events"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_events" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/history/parking"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="history_parking" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/alarms"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="alarms" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/events"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="events" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/live-camera"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="live_camera" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/task-management"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="task_management" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/my-tasks"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DashboardTabAliasRedirect tab="my_tasks" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/assignments"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/dashboard"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/map"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/history/playback"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/history/logs"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/history/events"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/history/parking"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/white-label"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/users"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/devices"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/alarms"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/events"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/live-camera"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/task-management"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/my-tasks"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard/*"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LegacyAdminDashboardRedirect currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        {/* ✅ Device control */}
        <Route
          path="/devices/:id/control"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DeviceControlWrapper />
            </ProtectedRoute>
          }
        />

        

        <Route
          path="/users"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <Navigate to={`/users/${DEFAULT_USER_ROLE_SECTION}`} replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/admin"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <Navigate to="/users/sub-admin" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/manager"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <UsersRolesPage section="manager" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/hr"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <Navigate to={`/users/${DEFAULT_USER_ROLE_SECTION}`} replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/office-employee"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <UsersRolesPage section="office-employee" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/field-employee"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <UsersRolesPage section="field-employee" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/:section"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <UsersRolesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/details"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <Navigate to="/logistic/trips" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/logistic/trips"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LogisticsModulePage section="trips" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/logistic/clients"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LogisticsModulePage section="clients" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/logistic/materials"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LogisticsModulePage section="materials" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/logistic/documents"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LogisticsModulePage section="documents" />
            </ProtectedRoute>
          }
        />

        {/* 🔒 Notifications Page */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/camera-portal"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              <CameraControlPortal />
            </ProtectedRoute>
          }
        />

        {/* 🔒 Task Management */}
        <Route
          path="/task-management"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              {demoReadOnly ? <Navigate to="/dashboard" replace /> : <Navigate to="/tasks/all" replace />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/tasks/all"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              {demoReadOnly ? <Navigate to="/dashboard" replace /> : <TasksModulePage section="all" />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/tasks/create"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              {demoReadOnly ? <Navigate to="/dashboard" replace /> : <TasksModulePage section="create" />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/tasks/inventory"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              {demoReadOnly ? <Navigate to="/dashboard" replace /> : <TasksModulePage section="inventory" />}
            </ProtectedRoute>
          }
        />

        {/* 🔒 My Tasks */}
        <Route
          path="/my-tasks"
          element={
            <ProtectedRoute requiredRoles={FIELD_WORKERS}>
              {demoReadOnly ? <Navigate to="/dashboard" replace /> : <MyTasks />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/licenses"
          element={
            <ProtectedRoute requiredRoles={CREDITS}>
              <LicensesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/credits"
          element={
            <ProtectedRoute requiredRoles={CREDITS}>
              {demoReadOnly ? <Navigate to="/licenses" replace /> : <CreditsTransfer />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/message"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              {demoReadOnly ? <Navigate to="/dashboard" replace /> : <MessagesPage />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/reminders"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <RemindersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/routes"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <Navigate to="/routes/list" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/routes/:section"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <RoutesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/expense"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <Navigate to="/expense/list" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/expense/create"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <ExpensePage section="create" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/expense/list"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <ExpensePage section="list" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/expense/upload"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <ExpensePage section="upload" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/sensors"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <Navigate to="/sensors/list" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/sensors/list"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <SensorsPage section="list" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/sensors/create"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <SensorsPage section="create" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/sensors/upload"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <SensorsPage section="upload" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/protocols"
          element={
            <ProtectedRoute requiredRoles={SUPERADMIN_ONLY_ROLES}>
              <ProtocolManagement />
            </ProtectedRoute>
          }
        />

        {/* 🔒 GROUP MANAGEMENT */}
        <Route
          path="/groups"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <GroupsPageShell>
                <GroupsList />
              </GroupsPageShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <InventoryTodo />
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/:id/edit"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <GroupsPageShell>
                <EditGroup />
              </GroupsPageShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/create"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <GroupsPageShell>
                <CreateGroup />
              </GroupsPageShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/edit/:id"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <GroupsPageShell>
                <CreateGroup />
              </GroupsPageShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/geofences"
          element={<Navigate to="/geofences/list" replace />}
        />

        <Route
          path="/geofences/list"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <GeofencesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/geofences/create"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <GeofencesPage />
            </ProtectedRoute>
          }
        />



        <Route
          path="/groups/:id"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <GroupsPageShell>
                <GroupDetail />
              </GroupsPageShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/:id/add-devices"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <AddDevices />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
